// src/components/artwork/forms/HeritageSection.jsx
// Statut patrimonial de l'œuvre et informations propres à une œuvre numérique.
// Colonnes : artworks.heritage_status, external_source, external_id, digital_asset (JSON).
// Aucun secret d'accès ne doit être saisi ici : seulement où se trouve l'accès, qui le détient, la procédure.
import React from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../context/AuthContext";
import { secretLevel } from "../../../utils/secretGuard";

const HERITAGE_OPTIONS = {
  gallery_stock: "Stock marchand de la galerie",
  personal: "Propriété personnelle",
  company: "Propriété de la société",
  deposit: "Dépôt",
  consignment: "Consignation",
  entrusted: "Œuvre confiée",
  to_clarify: "Statut à clarifier",
};

const DIGITAL_FIELDS = [
  ["format", "Média et format", "Ex. vidéo en boucle, fichier 4K", false],
  ["usage_rights", "Droits d'usage et d'affichage", "Ce que l'acquisition permet (affichage privé, public…)", true],
  ["resale_conditions", "Conditions de revente", "Revente possible, via qui, commission éventuelle", true],
  ["transmission_conditions", "Conditions connues de transmission", "Ce que la plateforme autorise ou n'autorise pas aux héritiers", true],
  ["display_equipment", "Équipement ou procédure d'affichage", "Écran, lecteur, installation fournie par l'artiste", true],
  ["access_custodian", "Qui détient l'accès", "Personne ou professionnel dépositaire (jamais l'accès lui-même)", false],
  ["access_procedure", "Procédure à suivre pour y accéder", "Où se trouve l'accès et quelles démarches suivre", true],
  ["heir_instructions", "Instructions laissées aux héritiers", "Souhaits du collectionneur", true],
];

const inputCls = "mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3";

// part : "all" (défaut), "status" (statut patrimonial seul, onglet Détails)
// ou "digital" (bloc œuvre numérique seul, onglet Art numérique).
export default function HeritageSection({ formData, setFormData, readOnly = false, isDigitalMedium = false, part = "all" }) {
  const { t } = useTranslation();
  const { profile } = useAuth();
  const digital = formData?.digital_asset && typeof formData.digital_asset === "object" ? formData.digital_asset : null;
  const showDigital = part === "digital" || isDigitalMedium || !!digital || !!formData?.external_source;
  // Le statut patrimonial (stock de la galerie, dépôt, consignation…) n'est proposé qu'aux comptes galerie.
  // Il reste visible s'il est déjà renseigné, et pour une personne qui édite l'œuvre d'un autre compte (conseiller, délégué).
  const editsForSomeoneElse = !!(formData?.client_id && profile?.id && formData.client_id !== profile.id);
  const showStatus = part !== "digital" && (profile?.gallery_mode === true || !!formData?.heritage_status || editsForSomeoneElse);
  const showDigitalBlock = part !== "status";
  // Référence au catalogue raisonné et auteur de l'authentification : mêmes comptes que le statut patrimonial.
  const showCatalogue = part !== "digital" && (profile?.gallery_mode === true || !!formData?.catalogue_raisonne_ref || !!formData?.authenticated_by || editsForSomeoneElse);

  const setField = (name, value) => setFormData((prev) => ({ ...(prev || {}), [name]: value }));
  const setDigital = (key, value) =>
    setFormData((prev) => {
      const current = prev?.digital_asset && typeof prev.digital_asset === "object" ? prev.digital_asset : {};
      return { ...(prev || {}), digital_asset: { ...current, [key]: value } };
    });

  return (
    <div className="space-y-4">
      {showStatus && (
      <div>
        <label htmlFor="heritage_status" className="block text-sm font-medium text-gray-700">
          {t("artworkEdit.heritage.status", { defaultValue: "Statut patrimonial" })}
        </label>
        <select
          id="heritage_status"
          name="heritage_status"
          value={formData?.heritage_status || ""}
          onChange={(e) => setField("heritage_status", e.target.value || null)}
          disabled={readOnly}
          className={inputCls}
        >
          <option value="">{t("artworkEdit.detail.select", { defaultValue: "Sélectionner" })}</option>
          {Object.entries(HERITAGE_OPTIONS).map(([key, label]) => (
            <option key={key} value={key}>
              {t(`artworkEdit.heritage.options.${key}`, { defaultValue: label })}
            </option>
          ))}
        </select>
        <p className="mt-1 text-xs text-gray-500">
          {t("artworkEdit.heritage.statusHint", {
            defaultValue: "À qui appartient l'œuvre : à distinguer du stock commercial et de la propriété de la société.",
          })}
        </p>
      </div>
      )}

      {showCatalogue && (
        <div className="grid gap-3">
          <div>
            <label htmlFor="catalogue_raisonne_ref" className="block text-xs font-medium text-gray-700">
              {t("artworkEdit.heritage.catalogueRef", { defaultValue: "Référence au catalogue raisonné" })}
            </label>
            <input id="catalogue_raisonne_ref" type="text" maxLength={200} value={formData?.catalogue_raisonne_ref || ""}
              onChange={(e) => setField("catalogue_raisonne_ref", e.target.value)} disabled={readOnly}
              placeholder={t("artworkEdit.heritage.catalogueRefPlaceholder", { defaultValue: "Ex. CR 214" })} className={inputCls} />
          </div>
          <div>
            <label htmlFor="authenticated_by" className="block text-xs font-medium text-gray-700">
              {t("artworkEdit.heritage.authenticatedBy", { defaultValue: "Authentifiée par" })}
            </label>
            <input id="authenticated_by" type="text" maxLength={200} value={formData?.authenticated_by || ""}
              onChange={(e) => setField("authenticated_by", e.target.value)} disabled={readOnly}
              placeholder={t("artworkEdit.heritage.authenticatedByPlaceholder", { defaultValue: "Estate, fondation, comité, expert" })} className={inputCls} />
          </div>
          <p className="text-xs text-gray-500">
            {t("artworkEdit.heritage.catalogueHint", { defaultValue: "Information saisie par vous : ARTICHAUT n'authentifie pas les œuvres et ne remplace pas le catalogue raisonné." })}
          </p>
        </div>
      )}

      {showDigitalBlock && !showDigital && !readOnly && (
        <button
          type="button"
          onClick={() => setField("digital_asset", {})}
          className="text-sm text-gray-600 underline hover:text-gray-900"
        >
          {t("artworkEdit.heritage.addDigital", { defaultValue: "Ajouter les informations d'œuvre numérique" })}
        </button>
      )}

      {showDigitalBlock && showDigital && (
        <div className="rounded-md border border-gray-200 bg-gray-50 p-4 space-y-3">
          <div className="text-sm font-semibold text-gray-800">
            {t("artworkEdit.heritage.digitalTitle", { defaultValue: "Œuvre numérique et accès associés" })}
          </div>
          <p className="rounded bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900">
            {t("artworkEdit.heritage.noSecret", {
              defaultValue:
                "N'inscrivez jamais de mot de passe, de clé privée ni de phrase de récupération. Indiquez seulement où se trouve l'accès, qui le détient et la procédure à suivre.",
            })}
          </p>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor="external_source" className="block text-xs font-medium text-gray-700">
                {t("artworkEdit.heritage.source", { defaultValue: "Plateforme source" })}
              </label>
              <input
                id="external_source"
                type="text"
                value={formData?.external_source || ""}
                onChange={(e) => setField("external_source", e.target.value)}
                disabled={readOnly}
                placeholder="Ex. sedition"
                className={inputCls}
              />
            </div>
            <div>
              <label htmlFor="external_id" className="block text-xs font-medium text-gray-700">
                {t("artworkEdit.heritage.externalId", { defaultValue: "Identifiant chez la plateforme" })}
              </label>
              <input
                id="external_id"
                type="text"
                value={formData?.external_id || ""}
                onChange={(e) => setField("external_id", e.target.value)}
                disabled={readOnly}
                className={inputCls}
              />
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor="source_url" className="block text-xs font-medium text-gray-700">
                {t("artworkEdit.heritage.sourceUrl", { defaultValue: "Page de l'œuvre sur la plateforme" })}
              </label>
              <input id="source_url" type="url" value={formData?.source_url || ""} onChange={(e) => setField("source_url", e.target.value)}
                disabled={readOnly} placeholder="https://" className={inputCls} />
            </div>
            <div>
              <label htmlFor="source_collection" className="block text-xs font-medium text-gray-700">
                {t("artworkEdit.heritage.sourceCollection", { defaultValue: "Collection thématique de la plateforme" })}
              </label>
              <input id="source_collection" type="text" value={formData?.source_collection || ""} onChange={(e) => setField("source_collection", e.target.value)}
                disabled={readOnly} className={inputCls} />
            </div>
          </div>
          {(formData?.imported_at || formData?.source_updated_at) && (
            <p className="text-xs text-gray-500">
              {formData?.imported_at ? `${t("artworkEdit.heritage.importedAt", { defaultValue: "Importée le" })} ${new Date(formData.imported_at).toLocaleDateString()}` : ""}
              {formData?.imported_at && formData?.source_updated_at ? " · " : ""}
              {formData?.source_updated_at ? `${t("artworkEdit.heritage.sourceUpdatedAt", { defaultValue: "Mise à jour côté plateforme le" })} ${new Date(formData.source_updated_at).toLocaleDateString()}` : ""}
            </p>
          )}

          {DIGITAL_FIELDS.map(([key, label, placeholder, multiline]) => {
            const value = digital?.[key] || "";
            const level = secretLevel(value);
            const Tag = multiline ? "textarea" : "input";
            return (
              <div key={key}>
                <label htmlFor={`digital_${key}`} className="block text-xs font-medium text-gray-700">
                  {t(`artworkEdit.heritage.digital.${key}`, { defaultValue: label })}
                </label>
                <Tag
                  id={`digital_${key}`}
                  {...(multiline ? { rows: 2 } : { type: "text" })}
                  value={value}
                  onChange={(e) => setDigital(key, e.target.value)}
                  disabled={readOnly}
                  placeholder={placeholder}
                  className={inputCls}
                />
                {level && (
                  <p className="mt-1 text-xs text-red-700">
                    {level === "block"
                      ? t("artworkEdit.heritage.secretBlock", {
                          defaultValue: "Ce champ semble contenir un secret d'accès : il ne pourra pas être enregistré.",
                        })
                      : t("artworkEdit.heritage.secretWarn", {
                          defaultValue: "Cette suite de mots ressemble à une phrase de récupération : ne l'enregistrez pas ici.",
                        })}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
